import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, LinearProgress, Typography,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { PRIVACY_POLICY_VERSION } from '../../config/appConfig';
import profileApi from '../Profile/profileApi';
import eventApi from './eventApi';
import ReferencePhotoRow from './ReferencePhotoRow';
import SelfieCaptureDialog from './SelfieCaptureDialog';
import './EventGallery.css';

const MAX_SIZE_BYTES = 10 * 1024 * 1024;

export default function PhotoRequestFormDialog({ open, onClose, eventId, inviteCode, onSuccess, mode = 'photos' }) {
  const { user } = useAuth();
  const isDeletionMode = mode === 'deletion';
  const title = isDeletionMode ? 'Add Deletion Request' : 'Add Photo Request';
  const bulkTitle = isDeletionMode ? 'Bulk Upload Deletion Requests' : 'Bulk Upload';
  const singleSubmitLabel = isDeletionMode ? 'Submit Deletion Request' : 'Submit Request';
  const bulkSubmitLabel = isDeletionMode ? 'Submit Bulk Deletion Requests' : 'Submit Bulk Requests';

  const [name, setName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [referencePhoto, setReferencePhoto] = useState(null);
  const [referenceConsent, setReferenceConsent] = useState(false);
  const [secondaryPhotos, setSecondaryPhotos] = useState([]); // [{photo_key, url, file}]
  const [bulkPhotos, setBulkPhotos] = useState([]);
  const [bulkProgress, setBulkProgress] = useState({ active: false, completed: 0, total: 0, failed: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selfieCamOpen, setSelfieCamOpen] = useState(false);

  // Reset form on open
  useEffect(() => {
    if (!open) return;
    setName('');
    setEmailAddress('');
    setPhoneNumber('');
    setNotes('');
    setReferencePhoto(null);
    setReferenceConsent(false);
    setSecondaryPhotos(prev => { prev.forEach(p => URL.revokeObjectURL(p.url)); return []; });
    setBulkPhotos([]);
    setBulkProgress({ active: false, completed: 0, total: 0, failed: 0 });
    setError('');
  }, [open]);

  // Pre-fill from profile
  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    setName(cur => cur || user.customer_name || '');
    setEmailAddress(cur => cur || user.email || '');
    profileApi.getProfile()
      .then(profile => {
        if (cancelled || !profile) return;
        setName(cur => cur || profile.customer_name || '');
        setEmailAddress(cur => cur || profile.email || '');
        setPhoneNumber(cur => cur || profile.phone || profile.extended?.alternate_phone || '');
      })
      .catch(() => {
        if (cancelled) return;
        setPhoneNumber(cur => cur || user.phone || user.phonenumber || '');
      });
    return () => { cancelled = true; };
  }, [open, user]);

  const previewUrl = useMemo(() => {
    if (!referencePhoto) return '';
    return URL.createObjectURL(referencePhoto);
  }, [referencePhoto]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  function onFileSelected(file) {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please upload an image file.'); return; }
    if (file.size > MAX_SIZE_BYTES) { setError('Image must be less than 10 MB.'); return; }
    setError('');
    setReferencePhoto(file);
  }

  function onSecondaryFileAdded(file) {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please upload an image file.'); return; }
    if (file.size > MAX_SIZE_BYTES) { setError('Image must be less than 10 MB.'); return; }
    setError('');
    const photo_key = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setSecondaryPhotos(prev => [...prev, { photo_key, url: URL.createObjectURL(file), file }]);
  }

  function onSecondaryFileRemoved(photoKey) {
    setSecondaryPhotos(prev => {
      const removed = prev.find(p => p.photo_key === photoKey);
      if (removed) URL.revokeObjectURL(removed.url);
      return prev.filter(p => p.photo_key !== photoKey);
    });
  }

  function onBulkFilesSelected(files) {
    const selected = Array.from(files || []);
    const valid = selected.filter(f => f.type.startsWith('image/') && f.size <= MAX_SIZE_BYTES);
    setBulkPhotos(valid);
    if (selected.length && valid.length !== selected.length) {
      setError('Some files were skipped - use images under 10 MB.');
    } else {
      setError('');
    }
  }

  function fileNameWithoutExtension(file) {
    return String(file?.name || 'Guest').replace(/\.[^/.]+$/, '').trim() || 'Guest';
  }

  async function submitSingle(e) {
    e.preventDefault();
    if (!name.trim() || !emailAddress.trim()) {
      setError('Please fill in name and email.');
      return;
    }
    if (isDeletionMode && !referencePhoto) { setError('Please upload a reference selfie.'); return; }
    if (referencePhoto && !referenceConsent) {
      setError(isDeletionMode ? 'Consent is required before deletion request face matching.' : 'Consent is required before reference selfie matching.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('invite_code', inviteCode);
      formData.append('name', name.trim());
      formData.append('email_address', emailAddress.trim());
      formData.append('notes', notes.trim());
      if (referencePhoto) formData.append('reference_photo', referencePhoto);
      if (isDeletionMode) {
        formData.append('consent_deletion_face_match', 'true');
        formData.append('policy_version', PRIVACY_POLICY_VERSION);
      } else {
        formData.append('phone_number', phoneNumber.trim());
        if (referencePhoto) formData.append('consent_reference_face_match', 'true');
        formData.append('policy_version', PRIVACY_POLICY_VERSION);
      }

      const res = isDeletionMode
        ? await eventApi.submitDeletionRequest(eventId, formData)
        : await eventApi.submitPhotoRequest(eventId, formData);
      if (res.error) { setError(res.error); return; }

      if (!isDeletionMode && referencePhoto && res.request_id && secondaryPhotos.length) {
        for (const secondary of secondaryPhotos) {
          try {
            await eventApi.addRequestSecondaryPhoto(eventId, res.request_id, secondary.file, { inviteCode });
          } catch {
            // Best-effort - the primary request already succeeded; a failed
            // secondary photo just means one fewer reference angle, not a
            // failed submission.
          }
        }
      }

      onSuccess(1);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to submit request.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function submitBulk() {
    if (!emailAddress.trim()) {
      setError(isDeletionMode ? 'Please fill in email for bulk deletion requests.' : 'Please fill in email for bulk requests.');
      return;
    }
    if (!bulkPhotos.length) { setError('Please select photos for bulk upload.'); return; }
    if (!referenceConsent) {
      setError(isDeletionMode ? 'Consent is required before bulk deletion request matching.' : 'Consent is required before bulk reference matching.');
      return;
    }

    setIsSubmitting(true);
    setBulkProgress({ active: true, completed: 0, total: bulkPhotos.length, failed: 0 });
    setError('');
    try {
      let failed = 0;
      for (let i = 0; i < bulkPhotos.length; i += 1) {
        const file = bulkPhotos[i];
        const formData = new FormData();
        formData.append('invite_code', inviteCode);
        formData.append('name', fileNameWithoutExtension(file));
        formData.append('email_address', emailAddress.trim());
        formData.append('notes', notes.trim());
        formData.append('reference_photo', file);
        if (isDeletionMode) {
          formData.append('consent_deletion_face_match', 'true');
          formData.append('policy_version', PRIVACY_POLICY_VERSION);
        } else {
          formData.append('phone_number', phoneNumber.trim());
          formData.append('consent_reference_face_match', 'true');
          formData.append('policy_version', PRIVACY_POLICY_VERSION);
        }
        try {
          const res = isDeletionMode
            ? await eventApi.submitDeletionRequest(eventId, formData)
            : await eventApi.submitPhotoRequest(eventId, formData);
          if (res.error) failed += 1;
        } catch {
          failed += 1;
        }
        setBulkProgress({ active: true, completed: i + 1, total: bulkPhotos.length, failed });
      }

      const successCount = bulkPhotos.length - failed;
      if (successCount <= 0) {
        setError('No requests were submitted. Check the selected files and try again.');
        return;
      }
      onSuccess(successCount);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to submit bulk requests.');
    } finally {
      setIsSubmitting(false);
      setBulkProgress(prev => ({ ...prev, active: false }));
    }
  }

  return (
    <>
    <SelfieCaptureDialog
      open={selfieCamOpen}
      onClose={() => setSelfieCamOpen(false)}
      onCapture={file => { onFileSelected(file); setSelfieCamOpen(false); }}
    />
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <form id="photo-request-form" onSubmit={submitSingle}>
          {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}

          <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Details</Typography>
          {user && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Pre-filled from your account - update if needed.
            </Typography>
          )}
          <div className="details-row">
            <input
              className="pr-form-input"
              type="text"
              placeholder="Full Name"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
            <input
              className="pr-form-input"
              type="email"
              placeholder="Email address"
              value={emailAddress}
              onChange={e => setEmailAddress(e.target.value)}
              required
            />
            {!isDeletionMode && (
              <input
                className="pr-form-input"
                type="tel"
                placeholder="Phone number (optional)"
                value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value)}
              />
            )}
          </div>
          <textarea
            className="pr-form-input pr-notes-input"
            placeholder="Notes for this request (optional)"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            maxLength={2000}
            rows={3}
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
            {isDeletionMode ? 'Reference Selfie' : 'Reference Selfie'}
          </Typography>
          {(referencePhoto || isDeletionMode) && (
            <FormControlLabel
              sx={{ alignItems: 'flex-start', mb: 1 }}
              control={
                <Checkbox
                  checked={referenceConsent}
                  onChange={e => setReferenceConsent(e.target.checked)}
                  required={Boolean(referencePhoto) || isDeletionMode}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  {isDeletionMode
                    ? 'I agree that CircleMoments may store this selfie and use face matching to identify photos linked to this face. CircleMoments assists with matching only; the organiser is responsible for actioning do-not-share requests.'
                    : 'I agree that CircleMoments may store this reference selfie and compare it with event face groups to find matching photos for this event.'}
                </Typography>
              }
            />
          )}
          {previewUrl ? (
            <div className="selfie-preview-block">
              <img src={previewUrl} alt="Reference preview" className="reference-preview" />
              <button type="button" className="selfie-change-btn" onClick={() => setReferencePhoto(null)}>
                Change photo
              </button>
            </div>
          ) : (
            <div className="selfie-capture-area">
              <div className="selfie-capture-row">
                <button type="button" className="selfie-open-cam-btn" onClick={() => setSelfieCamOpen(true)}>
                  <CameraAltIcon sx={{ fontSize: '1.1rem' }} />
                  {isDeletionMode ? 'Take Reference Selfie *' : 'Take Selfie'}
                </button>
                <label className="selfie-upload-icon-btn" title="Upload from gallery">
                  <input type="file" accept="image/*" onChange={e => onFileSelected(e.target.files?.[0])} />
                  <FileUploadOutlinedIcon fontSize="small" />
                </label>
              </div>
              <span className="selfie-capture-hint">or tap the upload icon to choose from gallery</span>
            </div>
          )}
          {!isDeletionMode && referencePhoto && (
            <ReferencePhotoRow
              photos={secondaryPhotos}
              onAdd={onSecondaryFileAdded}
              onRemove={onSecondaryFileRemoved}
              label="Additional reference photos (optional) - more angles help matching"
            />
          )}
        </form>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{bulkTitle}</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Select multiple reference photos. Each file name becomes the request name.
            {isDeletionMode ? ' Uses the same email entered above.' : ' Uses the same email (and phone number, if provided) entered above.'}
          </Typography>
          <label className="bulk-upload-drop">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={e => onBulkFilesSelected(e.target.files)}
            />
            <strong>{bulkPhotos.length ? `${bulkPhotos.length} files selected` : 'Choose bulk photos'}</strong>
            <span>{isDeletionMode ? 'Same email will be used for all.' : 'Same email (and phone number, if provided) will be used for all.'}</span>
          </label>
          {bulkPhotos.length > 0 && (
            <div className="bulk-file-list">
              {bulkPhotos.slice(0, 6).map(file => (
                <span key={`${file.name}-${file.size}`}>{fileNameWithoutExtension(file)}</span>
              ))}
              {bulkPhotos.length > 6 && <span>+{bulkPhotos.length - 6} more</span>}
            </div>
          )}
          {bulkProgress.total > 0 && (
            <div className="bulk-progress-wrap">
              <LinearProgress
                variant="determinate"
                value={(bulkProgress.completed / bulkProgress.total) * 100}
              />
              <span>
                {bulkProgress.completed} of {bulkProgress.total} submitted
                {bulkProgress.failed ? `, ${bulkProgress.failed} failed` : ''}
              </span>
            </div>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} disabled={isSubmitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="outlined"
          onClick={submitBulk}
          disabled={isSubmitting || !bulkPhotos.length}
          sx={{ textTransform: 'none' }}
        >
          {bulkProgress.active ? 'Submitting bulk...' : bulkSubmitLabel}
        </Button>
        <Button
          type="submit"
          form="photo-request-form"
          variant="contained"
          disabled={isSubmitting}
          sx={{ textTransform: 'none' }}
        >
          {isSubmitting && !bulkProgress.active
            ? <CircularProgress size={18} color="inherit" />
            : singleSubmitLabel}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
